"use client";

import {
  type ReactNode,
  type HTMLAttributes,
  useEffect,
  type FC,
  type Ref,
} from "react";

import { isDocumentDefined, mergeClasses } from "../../../utils";
import { FocusTrap } from "../FocusTrap";

import { backdropStyle } from "./Backdrop.css";

export type BackdropProps = HTMLAttributes<HTMLElement> & {
  delay?: number;
  hideOverflow?: boolean;
  children?: ReactNode;
  variant?: "default" | "opaque" | "clickThrough";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * The backdrop component is used to create a backdrop for a modal.
 */
export const Backdrop: FC<BackdropProps> = ({
  children,
  delay = 0,
  hideOverflow = true,
  className,
  variant = "default",
  ref,
  ...props
}) => {
  useEffect(() => {
    if (!hideOverflow || !isDocumentDefined()) {
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [hideOverflow, isDocumentDefined]);

  return (
    <FocusTrap
      ref={ref}
      className={mergeClasses(backdropStyle({ variant }), className)}
      transition={{ duration: 0.2 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { delay } }}
      exit={{ opacity: 0 }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as any)}
    >
      {children}
    </FocusTrap>
  );
};
